import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import type { LedgerSafeUnitSplitContext } from './ledgerSafeBoundaryEditing'
import {
  findLedgerSafeSuggestionAddedPrefixTokens,
  findLedgerSafeSuggestionAddedSuffixTokens,
  findLedgerSafeSuggestionRemovedPrefixTokens,
  findLedgerSafeSuggestionRemovedSuffixTokens,
  normalizeLedgerSafeSuggestionText,
  tokenizeLedgerSafeSuggestionText,
  tokenLedgerSafeSuggestionSequenceMatches
} from './ledgerSafeEditSuggestionScoring'
import {
  getLedgerSafeEventRowText,
  getLedgerSafeWitnessRowText
} from './ledgerSafeEditSuggestionRows'
import { normalizeLedgerSafeSplitToken } from './ledgerSafeSplitSuggestionEngine'
import { preprocessLedgerText } from './ledgerTextPreprocessing'

export type LedgerSafeBoundarySymptomSide = 'left-end' | 'right-start'

export interface LedgerSafeBoundarySymptom {
  side: LedgerSafeBoundarySymptomSide
  tokens: string[]
}

export const tokenizeLedgerSafeSplitSnapshotText = (text: string): string[] =>
  preprocessLedgerText(text).workspaceDisplayText.split(/\s+/).filter(Boolean)

export const normalizeLedgerSafeSplitSnapshotTokens = (tokens: string[]): string[] =>
  tokens.map(normalizeLedgerSafeSplitToken).filter(Boolean)

export const findLedgerSafeBestPrefixBoundary = (sourceTokens: string[], leftTokens: string[]): number | null => {
  if (sourceTokens.length < 2 || leftTokens.length === 0 || leftTokens.length >= sourceTokens.length) {
    return null
  }
  const sourceNormalized = normalizeLedgerSafeSplitSnapshotTokens(sourceTokens)
  const leftNormalized = normalizeLedgerSafeSplitSnapshotTokens(leftTokens)
  if (sourceNormalized.length < 2 || leftNormalized.length === 0 || leftNormalized.length >= sourceNormalized.length) {
    return null
  }

  let matched = 0
  while (
    matched < sourceNormalized.length &&
    matched < leftNormalized.length &&
    sourceNormalized[matched] === leftNormalized[matched]
  ) {
    matched += 1
  }
  if (matched === leftNormalized.length) {
    return matched
  }

  const boundary = Math.min(leftNormalized.length, sourceNormalized.length - 1)
  return boundary > 0 ? boundary : null
}

export const buildLedgerSafeSnapshotSplitContext = ({
  event,
  witnessId,
  rowId
}: {
  event: LedgerSafeEditorialEvent
  witnessId: string
  rowId: string
}): { context: LedgerSafeUnitSplitContext; splitBoundaryWordIndex: number } | null => {
  const beforeText = getLedgerSafeEventRowText(event, 'beforeRows', witnessId, rowId)
  const afterLeftText = getLedgerSafeEventRowText(event, 'afterRows', witnessId, rowId)
  if (
    !beforeText ||
    !afterLeftText ||
    normalizeLedgerSafeSuggestionText(beforeText) === normalizeLedgerSafeSuggestionText(afterLeftText)
  ) {
    return null
  }

  const beforeTokens = tokenizeLedgerSafeSplitSnapshotText(beforeText)
  const afterLeftTokens = tokenizeLedgerSafeSplitSnapshotText(afterLeftText)
  const boundary = findLedgerSafeBestPrefixBoundary(beforeTokens, afterLeftTokens)
  if (!boundary || boundary >= beforeTokens.length) {
    return null
  }

  const words = beforeTokens.map((token, index) => ({
    text: token,
    segmentableWordIndex: index,
    splitBoundaryWordIndex: index < beforeTokens.length - 1 ? index + 1 : null
  }))

  return {
    context: {
      witnessId,
      rowId,
      unitUuid: `${event.id}:${witnessId}:${rowId}:snapshot`,
      unitLabel: rowId,
      sourceText: beforeTokens.join(' '),
      unitStart: 0,
      unitEnd: beforeText.length,
      unitText: beforeTokens.join(' '),
      minSplitBoundaryWordIndex: 1,
      maxSplitBoundaryWordIndex: beforeTokens.length - 1,
      words
    },
    splitBoundaryWordIndex: boundary
  }
}

export const extractLedgerSafeBoundarySymptom = ({
  event,
  sourceWitnessId,
  leftRowId,
  rightRowId,
  deltaWords
}: {
  event: LedgerSafeEditorialEvent
  sourceWitnessId: string
  leftRowId: string
  rightRowId: string
  deltaWords: number
}): LedgerSafeBoundarySymptom | null => {
  const beforeLeft = getLedgerSafeEventRowText(event, 'beforeRows', sourceWitnessId, leftRowId)
  const afterLeft = getLedgerSafeEventRowText(event, 'afterRows', sourceWitnessId, leftRowId)
  const beforeRight = getLedgerSafeEventRowText(event, 'beforeRows', sourceWitnessId, rightRowId)
  const afterRight = getLedgerSafeEventRowText(event, 'afterRows', sourceWitnessId, rightRowId)
  if (!beforeLeft || !afterLeft || !beforeRight || !afterRight) {
    return null
  }
  if (deltaWords < 0) {
    const removedFromLeft = findLedgerSafeSuggestionRemovedSuffixTokens(beforeLeft, afterLeft)
    const addedToRight = findLedgerSafeSuggestionAddedPrefixTokens(beforeRight, afterRight)
    const tokens = removedFromLeft.length > 0 ? removedFromLeft : addedToRight
    return tokens.length > 0 ? { side: 'left-end', tokens } : null
  }
  if (deltaWords > 0) {
    const removedFromRight = findLedgerSafeSuggestionRemovedPrefixTokens(beforeRight, afterRight)
    const addedToLeft = findLedgerSafeSuggestionAddedSuffixTokens(beforeLeft, afterLeft)
    const tokens = removedFromRight.length > 0 ? removedFromRight : addedToLeft
    return tokens.length > 0 ? { side: 'right-start', tokens } : null
  }
  return null
}

export const targetStillShowsLedgerSafeBoundarySymptom = ({
  rows,
  targetWitnessId,
  leftRowId,
  rightRowId,
  symptom
}: {
  rows: AnchoredRow[]
  targetWitnessId: string
  leftRowId: string
  rightRowId: string
  symptom: LedgerSafeBoundarySymptom
}): boolean => {
  if (symptom.tokens.length === 0) {
    return false
  }
  if (symptom.side === 'left-end') {
    const leftTokens = tokenizeLedgerSafeSuggestionText(getLedgerSafeWitnessRowText(rows, leftRowId, targetWitnessId))
    return tokenLedgerSafeSuggestionSequenceMatches(leftTokens.slice(-symptom.tokens.length), symptom.tokens)
  }
  const rightTokens = tokenizeLedgerSafeSuggestionText(getLedgerSafeWitnessRowText(rows, rightRowId, targetWitnessId))
  return tokenLedgerSafeSuggestionSequenceMatches(rightTokens.slice(0, symptom.tokens.length), symptom.tokens)
}
